import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import ApiInstance from '../../../../helpers/apiInstance';
import actions from './actions';
import { fieldValidations, resolvedErrorMessage, sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { scrollToFirstError } from '../../../../helpers/scrollToFirstError';
import { isFinanceOrAdminOrSystemAdminRole } from '../../../../helpers/userRoles';
import { t } from '../../../../i18n';

const api = new ApiInstance('clients/:id');
const sectorsApi = new ApiInstance('sectors/list?pageSize=9999');
const clientsListApi = new ApiInstance('clients');
const clientsFormDataApi = new ApiInstance('clients/form_data');
const administratorsListApi = new ApiInstance('administrators/list');
const subUnitsApi = new ApiInstance('subunits/list');

function* loadData({ payload }) {
  try {
    const { id, role, isEdit } = payload;
    const result = yield call(sendRequest, api.get, { pathParams: { id } }, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      const { data } = result;
      yield put(
        actions.loadDataSuccess({
          data: { ...data, id },
        }),
      );

      if (isEdit) {
        yield put(
          actions.edit({
            id,
            role,
            client: {
              ...data,
              address: '',
              id,
            },
          }),
        );
      }
    } else if (result?.status === 404) {
      yield put(push('/404'));
    }
  } catch (error) {
    logger.error(error);
  }
}

const getParentId = client => {
  if (client.company) {
    return client.company;
  }

  if (client.division) {
    return client.division;
  }

  return null;
};

function* updateClient(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;

    const propsArray = Object.values(editForm);
    const client = keyByProperty(propsArray, 'name', prop => prop.value);

    if (!client.breaks_enabled) {
      client.breaks_duration = 0;
      client.breaks_compensation = false;
    }

    const { account_manager, activation_rep, sales_rep, subunits, state, ...otherProps } = client;
    const child_ids = subunits.map(item => item.value);
    const result = yield call(
      sendRequest,
      api.put,
      {
        data: {
          unit: {
            ...otherProps,
            account_manager_id: account_manager,
            activation_rep_id: activation_rep,
            child_ids,
            country_id: client.country,
            clocking_type: child_ids.length ? 'geoposition' : otherProps.clocking_type,
            in_person_clock_in_out: child_ids.length ? false : otherProps.in_person_clock_in_out,
            parent_id: getParentId(client),
            sales_rep_id: sales_rep,
            state_id: state,
          },
        },
        pathParams: { id },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.status === 200) {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', {
              item: t('dash.units.unit.unit_title'),
            }),
          }),
        );

        yield put(actions.saveSuccess());
        yield put(push(`/units/${result.data.id}`));
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteClient(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    const result = yield call(sendRequest, api.delete, { pathParams: { id } }, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      yield put(actions.deleteSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', {
            item: t('dash.units.unit.unit_title'),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addClient(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;

    const propsArray = Object.values(editForm);
    const client = keyByProperty(propsArray, 'name', prop => prop.value);

    const { account_manager, activation_rep, sales_rep, subunits, state, ...otherProps } = client;
    const child_ids = subunits.map(item => item.value);

    const result = yield call(
      sendRequest,
      clientsListApi.post,
      {
        data: {
          unit: {
            ...otherProps,
            account_manager_id: account_manager,
            activation_rep_id: activation_rep,
            sales_rep_id: sales_rep,
            child_ids,
            country_id: client.country,
            clocking_type: child_ids.length ? 'geoposition' : otherProps.clocking_type,
            parent_id: getParentId(client),
            state_id: state,
          },
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.status === 200) {
        const { id } = result.data;
        yield loadData({ payload: { id } });
        yield put(push(`/units/${id}/managers/add`));
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t('dash.units.unit.unit_title'),
            }),
          }),
        );
      } else {
        const validations = fieldValidations(result);

        // A refused write (403 and the like) resolves without field validations; say so instead of marking fields.
        if (validations) {
          yield put(actions.saveError({ data: validations }));
        } else {
          yield put(notifications.requestError({ error: resolvedErrorMessage(result) }));
        }
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    let { formData, sectors } = yield all({
      formData: call(sendRequest, clientsFormDataApi.get, {}, notifications.REQUEST_ERROR),
      sectors: call(sendRequest, sectorsApi.get, {}, notifications.REQUEST_ERROR),
    });

    if (formData?.status === 200 && formData?.data && sectors?.status === 200 && sectors?.data?.records) {
      formData = formData.data;
      sectors = sectors.data.records;
      yield put(actions.loadFormDataSuccess({ data: { ...formData, sectors } }));

      if (payload.id === 'add') {
        yield put(actions.create({ role: payload.role }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadManagersFormData({ payload }) {
  try {
    const isAcceptedRole = isFinanceOrAdminOrSystemAdminRole(payload.role);
    const result = isAcceptedRole
      ? yield call(sendRequest, administratorsListApi.get, { search: { pageSize: 2000 } }, notifications.REQUEST_ERROR)
      : null;

    if (result?.status === 200 && result?.data) {
      const { data } = result;

      yield put(
        actions.loadManagersFormData({
          data,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* filterByLegalEntity(data) {
  const isLegalEntityEnabled = yield select(state =>
    Boolean(state.StartAppSettings?.feature_flags?.legal_entity_enabled),
  );

  if (!isLegalEntityEnabled) return data;

  const legalEntityId = yield select(
    state => state.Client.client?.legal_entity?.id || state.Client.editForm?.legal_entity_id?.value,
  );

  if (!legalEntityId) return data;

  if (Array.isArray(data)) return data.filter(item => item.legal_entity_id === legalEntityId);

  if (Array.isArray(data?.data)) {
    return { ...data, data: data.data.filter(item => item.legal_entity_id === legalEntityId) };
  }

  return data;
}

function* filterSubUnits({ payload }) {
  try {
    const { filter } = payload;
    const result = filter
      ? yield call(
          sendRequest,
          subUnitsApi.get,
          { search: { filter, pageSize: 100, column: 'name' } },
          notifications.REQUEST_ERROR,
        )
      : null;

    if (result?.status === 200 && result?.data) {
      const { data } = result;
      const filtered = yield call(filterByLegalEntity, data);

      yield put(
        actions.filterSubUnitsSuccess({
          data: filtered,
        }),
      );
    } else if (!filter) {
      yield put(
        actions.filterSubUnitsSuccess({
          data: [],
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export { loadData };
export { loadFormData };
export { addClient };
export { updateClient };
export { deleteClient };
export { loadManagersFormData, filterSubUnits };
export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updateClient),
    takeEvery(actions.DELETE, deleteClient),
    takeEvery(actions.ADD, addClient),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.LOAD_FORM_DATA, loadFormData),
    takeEvery(actions.EDIT, loadManagersFormData),
    takeEvery(actions.CREATE, loadManagersFormData),
    takeEvery(actions.FILTER_SUB_UNITS, filterSubUnits),
    takeEvery(actions.SAVE_ERROR, scrollToFirstError),
  ]);
}
